<template>
  <v-container style="height: 91vh !important;" class="d-flex align-center justify-center" fluid>
    <v-row align="center" justify="center">
      <v-row class="mb-3 mt-n5" style="min-width: 77% !important; max-width: 77% !important;" align="center" justify="center">
        <v-col cols="8"><v-btn @click="backToTest">{{ $t('btnBackToTest') }}</v-btn></v-col>
        <v-spacer/>
        <v-col cols="4" class="mb-n3">
          <v-img :src="$store.state.fundingTest.evaluation.vendorLogoUrl" position="center right" max-height="37" m contain/>
        </v-col>
      </v-row>

      <!-- Funding Test Overview !-->
      <v-row align="center" justify="center">
        <v-card class="py-5 px-10" style="min-width: 75% !important; max-width: 75% !important;" dark outlined>
          <v-overlay :absolute="true" :value="overlayLoadingDetails"><v-progress-circular color="primary" :size="70" width="5" indeterminate/></v-overlay>
          <v-container fluid>
            <v-row align="center" justify="center">
              <v-col>
                <v-row align="center" justify="center">
                  <v-col cols="3">
                    <div class="d-flex align-center justify-start">
                      <v-icon color="#00F784" small>fa-calendar-week</v-icon>
                      <p class="white-content ml-2 my-0"
                         v-html="$tc('activityDays', this.totalDays, { days: this.totalDays })"/>
                    </div>
                    <div class="d-flex align-center justify-start mt-1">
                      <v-icon color="#00F784" small>fa-suitcase</v-icon>
                      <p class="white-content ml-2 my-0"
                         v-html="$t('brokerAccount', { broker: $store.state.fundingTest.account.brokerName, identifier: $store.state.fundingTest.account.identifier })"/>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <v-row align="center" justify="center">
                      <p class="white-content mt-n1 mb-1" v-html="$t('cycleSubtitle')"></p>
                    </v-row>
                    <v-row align="center" justify="center">
                      <h4 class="primary-color">{{ $t('start') }}</h4>
                      <h4 class="white-content mx-2">{{ getShortDate(new Date($store.state.fundingTest.currentCycleStartTime)) }}</h4>
                      <v-progress-linear height="7" rounded style="max-width: 35%" color="#00F784" :value="(activeDays * 100) / $store.state.fundingTest.evaluation.periodInDays"/>
                      <h4 class="primary-color mx-2">{{ $t('end') }}</h4>
                      <h4 class="white-content">{{ getShortDate(new Date($store.state.fundingTest.currentCycleEndTime)) }}</h4>
                    </v-row>

                  </v-col>
                  <v-col class="d-flex align-center justify-end" cols="3">
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-card class="px-3 py-1" v-bind="attrs" v-on="on" outlined>
                          <p class="my-0">{{ $t('payment.cyclesPrefix') }} <b style="font-size: 17px;" class="primary-color">{{ cycles }}</b> {{ $tc('payment.cyclesSuffix', cycles) }}</p>
                          <p class="my-0">{{ $t('payment.amount') }} <b style="font-size: 17px;" class="primary-color">${{ $store.state.fundingTest.currentSpending }}</b></p>
                        </v-card>
                      </template>
                      <p v-html="$t('payment.simulationDisclaimer')" class="my-0"/>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-row class="my-7" align="center" justify="center">
                  <CircularChart v-if="stringDataBalance !== null" :height="210" :primary-color="'#00F784'"
                                 :secondary-color="'#FFFFFF'" :string-data="stringDataBalance" :series="seriesBalance"/>
                  <CircularChart v-if="stringDataDailyLoss !== null" :height="210" :primary-color="'#FF6500'"
                                 :secondary-color="'#FFFFFF'" :string-data="stringDataDailyLoss" :series="seriesDailyLoss"/>
                  <CircularChart v-if="stringDataDrawdownLoss !== null" :height="210" :primary-color="'#FF6500'"
                                 :secondary-color="'#FFFFFF'" :string-data="stringDataDrawdownLoss" :series="seriesDrawdownLoss"/>
                </v-row>
                <v-row>
                  <v-col cols="10">
                    <RulesOverview :collapsed="false"/>
                  </v-col>
                  <v-col class="d-flex align-end justify-end" cols="2">
                    <AbandonButton @confirmAbandon="confirmAbandon"/>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-row>
    </v-row>

    <!-- Overlay confirm abandon!-->
    <v-overlay v-model="overlayAbandon">
      <AbandonTest @close="overlayAbandon = false"/>
    </v-overlay>
  </v-container>
</template>

<script>
import Repository from '../../../../repositories/funding';
import { mapMutations } from 'vuex';
import CircularChart from "@/views/Funding/Simulation/components/CircularChart";
import RulesOverview from "@/views/Funding/Simulation/components/RulesOverview";
import AbandonButton from "@/views/Funding/Simulation/components/AbandonButton";
import AbandonTest from "@/views/Funding/Simulation/components/AbandonTest";

export default {
  name: "TestOverview",
  components: {AbandonTest, AbandonButton, RulesOverview, CircularChart},
  props: {
    testId: Number,
  },
  data() {
    return {
      overlayLoadingDetails: false,
      overlayAbandon: false,

      // Days Data
      months: [],
      totalDays: 0,
      activeDays: 0,
      remainingDays: 0,
      cycles: 1,

      // Main Stats
      stringDataBalance: null,
      stringDataDailyLoss: null,
      stringDataDrawdownLoss: null,
      seriesBalance: [0],
      seriesDailyLoss: [0],
      seriesDrawdownLoss: [0],
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    ...mapMutations(['setFundingTest']),
    init() {
      this.getMonths();
      this.overlayLoadingDetails = true;

      if(this.testId === undefined && this.$store.state.fundingTest === null)
        this.$router.push({ name: 'funding-simulation-home' });

      let testId = (this.testId !== undefined)? this.testId : this.$store.state.fundingTest.id;
      Repository.fundingTestOverview(testId).then(async (response) => {
        if(response.status < 400) {
          this.overlayLoadingDetails = false;
          await this.setFundingTest(response.data);
          await this.processPerformance();
        }
      });
    },
    processPerformance() {
      this.calculateDays();
      this.processChartData();
    },
    calculateDays() {
      //let Difference_In_Time_Total = new Date(this.$store.state.fundingTest.nextPaymentTime).getTime() - new Date(this.$store.state.fundingTest.startTime).getTime();
      let Difference_In_Time_Total = Math.abs(new Date().getTime() - new Date(this.$store.state.fundingTest.createdAt).getTime());
      this.totalDays = Math.floor(Difference_In_Time_Total / (1000 * 3600 * 24));

      let Difference_In_Time_Remaining = Math.abs(new Date(this.$store.state.fundingTest.currentCycleEndTime).getTime() - new Date().getTime());
      this.remainingDays = Math.floor(Difference_In_Time_Remaining / (1000 * 3600 * 24));
      this.activeDays = this.$store.state.fundingTest.evaluation.periodInDays - this.remainingDays;

      this.cycles = Math.ceil(this.totalDays / this.$store.state.fundingTest.evaluation.periodInDays);
    },
    processChartData() {
      // Target
      let totalProfit = Math.abs(Number(this.$store.state.fundingTest.evaluation.initialBalance) - Number(this.$store.state.fundingTest.evaluation.target));
      let reachedProfit =  (Number(this.$store.state.fundingTest.currentProfit) / totalProfit) * 100;
      this.stringDataBalance = {
        firstTitle: this.$t('target.targetTitle'),
        firstNumber: Number(this.$store.state.fundingTest.evaluation.target) - Number(this.$store.state.fundingTest.evaluation.initialBalance),
        secondTitle: this.$t('target.currentProfitTitle'),
        secondNumber: Number(this.$store.state.fundingTest.currentProfit),
      };
      this.seriesBalance = [reachedProfit];

      // Daily Loss
      let ruleStatusForDailyLoss = this.$store.state.fundingTest.ruleStatuses.find((ruleStatus) => ruleStatus.rule === 'MAX_DAILY_LOSS');
      if(ruleStatusForDailyLoss !== undefined) {
        let reachedDailyLoss = (Math.abs(ruleStatusForDailyLoss.currentValue) / Math.abs(Number(ruleStatusForDailyLoss.referenceValue))) * 100;
        this.stringDataDailyLoss = {
          firstTitle: this.$t('dailyLoss.dailyLossTitle'),
          firstNumber: Math.abs(Number(ruleStatusForDailyLoss.referenceValue)),
          secondTitle: this.$t('dailyLoss.todayLossTitle'),
          secondNumber: Math.abs(Number(ruleStatusForDailyLoss.currentValue)),
        };
        this.seriesDailyLoss = [reachedDailyLoss];
      }

      // Drawdown Loss
      let ruleStatusForDrawdownLoss = this.$store.state.fundingTest.ruleStatuses.find((ruleStatus) => ruleStatus.rule === 'MAX_DRAWDOWN');
      if (ruleStatusForDrawdownLoss !== undefined) {
        let reachedDrawdownLoss = (Math.abs(ruleStatusForDrawdownLoss.currentValue) / Math.abs(Number(ruleStatusForDrawdownLoss.referenceValue))) * 100;
        this.stringDataDrawdownLoss = {
          firstTitle: this.$t('drawdownLoss.drawdownTitle'),
          firstNumber: Math.abs(Number(ruleStatusForDrawdownLoss.referenceValue)),
          secondTitle: this.$t('drawdownLoss.currentTotalLossTitle'),
          secondNumber: Math.abs(Number(ruleStatusForDrawdownLoss.currentValue)),
        };
        this.seriesDrawdownLoss = [reachedDrawdownLoss];
      }
    },
    getShortDate(date) {
      return `${this.months[date.getMonth()]} ${date.getDate()}`;
    },
    getMonths() {
      this.months = [
        this.$t('months.jan'),
        this.$t('months.feb'),
        this.$t('months.mar'),
        this.$t('months.apr'),
        this.$t('months.may'),
        this.$t('months.jun'),
        this.$t('months.jul'),
        this.$t('months.aug'),
        this.$t('months.sep'),
        this.$t('months.oct'),
        this.$t('months.nov'),
        this.$t('months.dec'),
      ];
    },
    confirmAbandon() {
      this.overlayAbandon = true;
    },
    backToTest() {
      this.$router.push({ name: 'funding-simulation-home' });
    }
  }
}
</script>

<i18n>
{
  "en": {
    "title": "Your simulation on {market}",
    "cycleSubtitle": "Current Cycle",
    "start": "Start",
    "end": "End",
    "activityDays": "You have <b>{days} days</b> of evaluation",
    "brokerAccount": "Participating with <b>{identifier}</b>",
    "payment": {
      "cyclesPrefix": "You have",
      "cyclesSuffix": "cycle | cycles",
      "amount": "Current Total",
      "simulationDisclaimer": "This is what you would have normally<br>paid in the current evaluation.<br>With <b style='color: #00F784'>Swiset</b> you can simulate unlimited times."
    },
    "target": {
      "targetTitle": "Target Profit",
      "currentProfitTitle": "Current Profit"
    },
    "dailyLoss": {
      "dailyLossTitle": "Max Daily Loss",
      "todayLossTitle": "Today's Loss"
    },
    "drawdownLoss": {
      "drawdownTitle": "Max Drawdown",
      "currentTotalLossTitle": "Drawdown"
    },
    "months": {
      "jan" : "Jan",
      "feb" : "Feb",
      "mar" : "Mar",
      "apr" : "Apr",
      "may" : "May",
      "jun" : "Jun",
      "jul" : "Jul",
      "aug" : "Aug",
      "sep" : "Sep",
      "oct" : "Oct",
      "nov" : "Nov",
      "dec" : "Dec"
    },
    "btnBackToTest": "« Evaluations"
  },
  "es": {
    "vendorPrefix": "Simulando una prueba de",
    "title": "Tú simulación en {market}",
    "cycleSubtitle": "Ciclo Actual",
    "start": "Inicio",
    "end": "Fin",
    "activityDays": "Llevas <b>{days} día</b> de prueba | Llevas <b>{days} días</b> de prueba",
    "brokerAccount": "Participando con <b>{identifier}</b>",
    "payment": {
      "cyclesPrefix": "Llevas",
      "cyclesSuffix": "ciclo | ciclos",
      "amount": "Total Actual",
      "simulationDisclaimer": "Esto es lo que hubieras pagado<br>normalmente en la prueba real.<br>Con <b style='color: #00F784'>Swiset</b> puedes simular ilimitadas veces."
    },
    "target": {
      "targetTitle": "Rendimiento Objetivo",
      "currentProfitTitle": "Rendimiento Actual"
    },
    "dailyLoss": {
      "dailyLossTitle": "Límite Diario de Pérdida",
      "todayLossTitle": "Pérdida Hoy"
    },
    "drawdownLoss": {
      "drawdownTitle": "Máximo Drawdown",
      "currentTotalLossTitle": "Drawdown"
    },
    "months": {
      "jan" : "Ene",
      "feb" : "Feb",
      "mar" : "Mar",
      "apr" : "Abr",
      "may" : "May",
      "jun" : "Jun",
      "jul" : "Jul",
      "aug" : "Aug",
      "sep" : "Sep",
      "oct" : "Oct",
      "nov" : "Nov",
      "dec" : "Dec"
    },
    "btnBackToTest": "« Pruebas"
  }
}
</i18n>

<style lang="scss" scoped>
@import "src/global";

.primary-color {
  color: $primary-color;
}

.white-content {
  color: $white-color;
}

.gray-content {
  color: #BDBDBD;
}
</style>
